/**
 * DI-3-P6 — the drop is over when the last set has left (Intake Workflows v2
 * §2 step 13: the drop ends with the last verification).
 *
 * `sessionDone(state, answers)` — true when EVERY started set of this tab's
 * runner has left the intake AND nothing is pending in the browser:
 *
 *   set                                                   left?
 *   ─────────────────────────────────────────────────────  ───────────────────
 *   no acquisition yet (decided / creating)                no — local work
 *   a refused create (error row, no acquisition)           no — M3a has no
 *                                                          dismiss: "Start
 *                                                          over" drops it
 *   status not read yet                                    no
 *   status `intakeState: null` (finalized by Approve, or   yes
 *     promoted by the ingest after the upload)
 *   status read 404 (withdrawn: REJECTED + deleted)        yes
 *   a Move (the acquisition kept, a new run)               no — its state
 *   `withheld_complete` (a partner set, not approved)      no — until Approve
 *                                                          finalizes it
 *   `approved_pending_upload` (N2 "after verification")    no — the state stays
 *                                                          until the ingest
 *                                                          promotes the set
 *   any other failed read (5xx, network)                   no
 *
 * Every set must have reached `running` (the browser's hand-over done — a
 * 404 for a set this tab has not handed over yet never ends anything), and
 * then `hasPendingLocalWork` over the sets that are still on the server
 * must be false: a finalized set whose bytes are still going keeps the session
 * alive (N2 — never end a session with bytes pending). A WITHDRAWN set's own
 * pending upload does not count: its acquisition is gone, its bytes have
 * nowhere to go. "Left" is `api/intake.ts:leftTheIntake` — the one predicate
 * the verify list and `?open=` read too.
 *
 * When it turns true the runner releases the session (`intakeRunner.release`:
 * the record kept with `endedAt`, the active session forgotten, the worker,
 * the rows and the runner state gone) and the ONE bottom status bar says
 * "Drop done · n data sets verified" for a few seconds (`sessionEndStore`).
 * The board evaluates it with the statuses it polls; the Upload page, where
 * no board is mounted, reads the same query keys (`useSessionEndWatch`).
 */
import { useEffect, useMemo } from "react";
import { createStore, useStore } from "zustand";
import { useQueryClient } from "@tanstack/react-query";
import {
  goneFromIntake,
  leftTheIntake,
  useIntakeStatuses,
} from "../api/intake.js";
import { intakeKeys } from "../api/keys.js";
import type { IntakeStatus } from "../api/intakeTypes.js";
import { uploadPromoted } from "../upload/state.js";
import { intakeRunner, type IntakeRunner } from "./intakeRunner.js";
import {
  hasPendingLocalWork,
  useRunnerState,
  type RunnerSet,
  type RunnerState,
} from "./runnerStore.js";

/** What the server said about one acquisition (a status query's data + error). */
export interface SetAnswer {
  status: IntakeStatus | null | undefined;
  error?: unknown;
}

/** acquisition id → its answer. */
export type SetAnswers = Readonly<Record<string, SetAnswer | undefined>>;

/** The acquisition a runner set stands for (created, or an add's bound one). */
export function setAcquisition(s: RunnerSet): string | null {
  return s.created?.dataAcquisitionId ?? s.boundTo ?? null;
}

/** The answers of the status queries, keyed by acquisition id. */
export function answersOf(
  daIds: readonly (string | null)[],
  queries: readonly { data?: IntakeStatus | undefined; error?: unknown }[],
): Record<string, SetAnswer> {
  const out: Record<string, SetAnswer> = {};
  daIds.forEach((da, k) => {
    const q = queries[k];
    if (da && q) out[da] = { status: q.data, error: q.error };
  });
  return out;
}

/** Every started set left the intake and nothing is pending in this tab (see the table above). */
export function sessionDone(
  state: Pick<RunnerState, "started" | "sets">,
  answers: SetAnswers,
): boolean {
  if (!state.started || state.sets.length === 0) return false;
  const onServer: RunnerSet[] = [];
  for (const s of state.sets) {
    // the browser's own part first: a set not yet handed over (create, hash,
    // metadata — or stopped on an error) holds the session whatever the
    // server says about its acquisition
    if (s.phase !== "running") return false;
    const da = setAcquisition(s);
    if (!da) return false;
    const a = answers[da];
    if (!a || !leftTheIntake(a.status, a.error)) return false;
    if (!goneFromIntake(a.error)) onServer.push(s);
  }
  return !hasPendingLocalWork({ started: true, sets: onServer });
}

/** How the sets left: verified (finalized / promoted) vs withdrawn (gone). */
export function sessionSummary(
  state: Pick<RunnerState, "sets">,
  answers: SetAnswers,
): { verified: number; withdrawn: number } {
  let verified = 0;
  let withdrawn = 0;
  for (const s of state.sets) {
    const da = setAcquisition(s);
    if (da && goneFromIntake(answers[da]?.error)) withdrawn += 1;
    else verified += 1;
  }
  return { verified, withdrawn };
}

/** "Drop done · 3 data sets verified" (· "1 withdrawn"; "1 data set withdrawn" when none was verified). */
export function doneLine(verified: number, withdrawn: number): string {
  const sets = (n: number): string =>
    `${String(n)} ${n === 1 ? "data set" : "data sets"}`;
  if (verified === 0) return `Drop done · ${sets(withdrawn)} withdrawn`;
  const head = `Drop done · ${sets(verified)} verified`;
  return withdrawn > 0 ? `${head} · ${String(withdrawn)} withdrawn` : head;
}

// ── the status bar's line ───────────────────────────────────────────────

/** How long the bottom bar says "Drop done · …". */
export const DONE_LINE_MS = 6_000;

export interface SessionEndState {
  /** The line the bottom status bar shows (null = none). */
  line: string | null;
  /** The last session that ended in this tab (the Upload page drops its "Continue"). */
  endedSessionId: string | null;
}

export const sessionEndStore = createStore<SessionEndState>(() => ({
  line: null,
  endedSessionId: null,
}));

let lineTimer: ReturnType<typeof setTimeout> | null = null;

export function announceSessionEnd(sessionId: string, line: string): void {
  if (lineTimer !== null) clearTimeout(lineTimer);
  sessionEndStore.setState({ line, endedSessionId: sessionId });
  lineTimer = setTimeout(() => {
    lineTimer = null;
    sessionEndStore.setState({ line: null });
  }, DONE_LINE_MS);
}

/** Tests. */
export function resetSessionEnd(): void {
  if (lineTimer !== null) clearTimeout(lineTimer);
  lineTimer = null;
  sessionEndStore.setState({ line: null, endedSessionId: null });
}

export function useSessionEndState<T>(pick: (s: SessionEndState) => T): T {
  return useStore(sessionEndStore, pick);
}

// ── the hooks ───────────────────────────────────────────────────────────

/**
 * Release this tab's session once `sessionDone` holds for the given answers.
 * Also: a set the ingest promoted ("after verification", `present` +
 * PROCESSING) had its status poll stopped on the old verdict — its status is
 * read again, so the server's `intakeState: null` arrives.
 */
export function useSessionEnd(
  answers: SetAnswers,
  runner: IntakeRunner = intakeRunner,
): void {
  const state = useRunnerState(runner.state, (s) => s);
  const qc = useQueryClient();
  const promoted = state.sets
    .filter((s) => uploadPromoted(s.upload))
    .map(setAcquisition)
    .filter((x): x is string => Boolean(x))
    .join("|");
  useEffect(() => {
    for (const da of promoted.split("|"))
      if (da) void qc.invalidateQueries({ queryKey: intakeKeys.status(da) });
  }, [promoted, qc]);

  const done = sessionDone(state, answers);
  const { verified, withdrawn } = sessionSummary(state, answers);
  const sid = state.sessionId;
  useEffect(() => {
    if (!done || !sid) return;
    // said BEFORE the release clears the runner: the board must not redraw
    // the ended session from its last-read record for a tick
    sessionEndStore.setState({ endedSessionId: sid });
    void runner.release().then((ended) => {
      if (ended) announceSessionEnd(ended, doneLine(verified, withdrawn));
    });
  }, [done, sid, runner, verified, withdrawn]);
}

/**
 * The Upload page's watcher (no board is mounted there): this tab's runner
 * sets' statuses — the same query keys and poll rule as the board's cards.
 */
export function useSessionEndWatch(runner: IntakeRunner = intakeRunner): void {
  const sets = useRunnerState(runner.state, (s) => s.sets);
  const daIds = useMemo(() => sets.map(setAcquisition), [sets]);
  const queries = useIntakeStatuses(
    sets.map((s, k) => ({
      daId: daIds[k] ?? null,
      localPending: s.phase !== "running" && s.error === null,
    })),
  );
  useSessionEnd(answersOf(daIds, queries), runner);
}
